// @flow

import queryString from 'query-string';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Heading, Button, Paragraph, Picture, MediaQuery } from '@riseart/common';
import { ContinuousSlider } from '@riseart/slider';
import { CloseIcon, RefreshIcon, VisuallySimilarIcon } from '@riseart/icons';
import { GOOGLE_TAGMANAGER_ACTIONS } from 'shared_data/providers/google/tagmanager/Actions';
import { actionFactory } from 'shared_services/riseart/utils/Utils';
import { HelpCard } from 'shared_components/common/HelpCard';
import { VisualSearchCard } from 'shared_components/forms/search/visual/Card';
import { VisualSearchCardMenu } from 'shared_components/forms/search/visual/CardMenu';
import { dataToBasicArtProps } from 'shared_services/riseart/utils/artDataUtils';

import { raScreenLg } from '@riseart/antd-provider/dist/website/variables.less';
import {
  visualSearchCls,
  visualSearchHeadingCls,
  visualSearchContentCls,
  visualSearchControlsCls,
} from 'shared_components/forms/search/visual/Visual.less';

type Props = {
  title: Node | string,
  journey: Object,
  onGoToResults: Function,
  urlAssembler: Function,
};

/**
 * VisualSearch
 *
 * @param {props} Props
 * @returns {Node}
 */
export function VisualSearch({ title, journey, onGoToResults, urlAssembler }: Props): Node {
  const history = useHistory();
  const dispatch = useDispatch();
  const [searchItems, setSearchItems] = useState([]);
  const [menuOptions, setMenuOptions] = useState(null);
  const historyUnlisten = useRef();
  const sliderRef = useRef();
  const callGtmAction = useCallback(
    (action: string) => {
      dispatch(actionFactory(action, {}));
    },
    [dispatch],
  );
  const totalSearchedItems = searchItems.length;

  useEffect(() => {
    // Listen for history pop action and try to find pop item in current searchItems state
    historyUnlisten.current = history.listen((state, action) => {
      if (action === 'POP') {
        const { v } = queryString.parse(state.search);
        const foundItem = searchItems.reduce((accumulator, item, idx) => {
          if (item.item.id === v) {
            const previousItem = searchItems[idx - 1];
            return { idx, ...item, journeyId: (previousItem && previousItem.journeyId) || null };
          }

          return accumulator;
        }, null);

        setSearchItems(foundItem ? searchItems.slice(0, foundItem.idx) : []);
      }
    });

    return historyUnlisten.current;
  }, [searchItems, history]);

  useEffect(() => {
    if (
      journey &&
      journey.journeyId &&
      searchItems.filter((item) => item.journeyId === journey.journeyId).length === 0
    ) {
      const lastItem = searchItems[searchItems.length - 1];
      setMenuOptions(null);

      // If last item is same art, then replace it in the state
      if (lastItem && lastItem.item && lastItem.item.id === journey.item.id) {
        setSearchItems([
          ...searchItems.filter((item) => item.item.id !== lastItem.item.id),
          journey,
        ]);
      } else {
        // If journey.item is not in the state, then add it
        setSearchItems([...searchItems, journey]);
      }

      setTimeout(() => sliderRef.current && sliderRef.current.scrollToEnd(), 100);
    }
  }, [journey, searchItems]);

  const handleRefresh = useCallback(() => {
    const lastItem = searchItems[searchItems.length - 1];
    callGtmAction(GOOGLE_TAGMANAGER_ACTIONS.VISUAL_SEARCH_REFRESH);
    setMenuOptions(null);
    setSearchItems(searchItems.slice(0, -1));
    onGoToResults({ refresh: true, ...lastItem });
  }, [searchItems, onGoToResults, callGtmAction]);

  const handleRestart = useCallback(() => {
    sliderRef.current && sliderRef.current.scrollToStart();
    callGtmAction(GOOGLE_TAGMANAGER_ACTIONS.VISUAL_SEARCH_RESTART);
    setMenuOptions(null);
    setSearchItems([]);
  }, [sliderRef, callGtmAction]);

  return (
    <section className={visualSearchCls}>
      <Heading level="5" tag="h3" className={visualSearchHeadingCls}>
        {title}
      </Heading>

      <MediaQuery minWidth={raScreenLg}>
        {(isLargeScreen: boolean) => (
          <div className={visualSearchContentCls}>
            {isLargeScreen && menuOptions ? (
              <VisualSearchCardMenu {...menuOptions} overlay />
            ) : null}
            <ContinuousSlider
              nextArrowText="common.next"
              previousArrowText="common.previous"
              ref={sliderRef}
            >
              {searchItems.map(({ item }, idx) => {
                const { pictureProperties } = dataToBasicArtProps(
                  item,
                  { basePath: 'en', isDefault: true, language: 'en', name: 'en-GB' },
                  'slider.gridCommon',
                );

                const previousItem = idx ? searchItems[idx - 1] : null;

                return (
                  <VisualSearchCard
                    key={idx}
                    includeMenu={!isLargeScreen}
                    item={item}
                    onMenuClick={setMenuOptions}
                    onBackToResult={
                      // Show the option only for items other than the last
                      idx < totalSearchedItems - 1
                        ? () => {
                            setSearchItems(searchItems.slice(0, idx));
                            onGoToResults({
                              item,
                              ...(previousItem && previousItem.journeyId
                                ? { journeyId: previousItem.journeyId }
                                : null),
                            });
                          }
                        : null
                    }
                  >
                    <Picture {...pictureProperties} />
                  </VisualSearchCard>
                );
              })}
              <HelpCard icon={<VisuallySimilarIcon />}>
                <Paragraph>
                  <FormattedMessage id="forms.search.visual.help">
                    {(text) => text}
                  </FormattedMessage>
                </Paragraph>
              </HelpCard>
            </ContinuousSlider>
          </div>
        )}
      </MediaQuery>

      {searchItems.length ? (
        <div className={visualSearchControlsCls}>
          <Button type="secondary" size="small" icon={<RefreshIcon />} onClick={handleRefresh}>
            <FormattedMessage id="forms.search.visual.refresh" />
          </Button>
          <Button
            isAnchor
            obfuscate
            type="secondary"
            size="small"
            href={urlAssembler('')}
            onClick={handleRestart}
          >
            <CloseIcon />
            <span>
              <FormattedMessage id="forms.search.visual.restart" />
            </span>
          </Button>
        </div>
      ) : null}
    </section>
  );
}
